import { select } from "@angular-redux/store";
import { Component, forwardRef, Inject, OnInit, ViewChild } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { AccountService, BillingService, DialogCreationService, PlanService, RedirectService } from "sharedFeatures/sharedServices";
import { currencySymbols, currencyUtcSymbols, LicenseFrequencyType, LicensePackageType } from "../../../enums";
import { Account, paymentHistory, SpareLicenseModel } from "../../../sharedModels";
import { AuthData, LicenseData } from "../../../sharedModels/AuthData";
import { IErrorResponse, IGetActivePlansCountResponse, isSuccessResponse } from "../../../sharedServices/Responses/plan.index";
import { AuthUtils, NavigationUtils, Utils } from "../../../utils";
import { ItemData } from "../form";
import { BuyPlanWinComponent } from "./buy-plan-win.component";
import { takeUntil } from "rxjs/operators";
import { BillingCycleInfo } from "../../../sharedModels/billingCycleInfo";
import * as moment from "moment";

@Component({
    selector: 'buy-plan-win-body',
    templateUrl: './buy-plan-win-body.component.html',
    styleUrls: ['./buy-plan-win-body.component.less']
})

export class BuyPlanWinBodyComponent {

    isBusinessDoctors: boolean = false;

    constructor(
        private planService: PlanService,
        private billingService: BillingService,
        private redirectService: RedirectService,
        private accountService: AccountService,
        @Inject(forwardRef(() => BuyPlanWinComponent)) public parent: BuyPlanWinComponent) {
        this.isBusinessDoctors = (String)(window['isBusinessDoctors']).toLowerCase() === 'true';
    }

    onClose() {
        this.parent.close();
    }

    brixxSinglePlansCountItems: Array<ItemData> = [
        { id: '1', value: '1 Plan Included', elementId: 1, disabled: false, },
        { id: '2', value: '2 Plans', elementId: 2, disabled: false, },
        { id: '3', value: '3 Plans', elementId: 3, disabled: false, },
        { id: '4', value: '4 Plans', elementId: 4, disabled: false, },
        { id: '5', value: '5 Plans', elementId: 5, disabled: false, },
        { id: '6', value: '6 Plans', elementId: 6, disabled: false, },
        { id: '7', value: '7 Plans', elementId: 7, disabled: false, },
        { id: '8', value: '8 Plans', elementId: 8, disabled: false, },
        { id: '9', value: '9 Plans', elementId: 9, disabled: false, },
        { id: '10', value: '10 Plans', elementId: 10, disabled: false, },
        { id: '11', value: '15 Plans', elementId: 15, disabled: false, },
        { id: '12', value: '20 Plans ', elementId: 20, disabled: false, },
        { id: '13', value: '30 Plans', elementId: 30, disabled: false, },
        { id: '14', value: '40 Plans', elementId: 40, disabled: false, },
        { id: '15', value: '50 Plans', elementId: 50, disabled: false, },
    ];

    prices: Record<string, string[][]> | undefined = undefined;

    // Unsubscribe event
    private storeUnsubscribe = new Subject<void>();

    @select(['account', 'accountData'])
    readonly accountData$!: Observable<Account>;
    private accountData: Account = new Account();

    @select(['account', 'authData'])
    readonly authData$!: Observable<AuthData>;
    authData: AuthData = new AuthData();

    licenses: Array<SpareLicenseModel> = [];

    initialSubscriptionType: LicenseFrequencyType = 'None';
    selectedSubscriptionType: LicenseFrequencyType = 'None';

    initialPackageType = LicensePackageType.None;
    initialLicenseStatus: string = '';
    initialPriceOptionCodes: string[] = [];
    initialLicenseEndDate: string = '';

    selectedPlansItemId!: number;
    selectedPlansCount: string = '1';
    initialPlanCount: number = 0;

    currentCurrencyId: number = 1;
    currentCurSymbol: string = '$';
    selectedCurCurrency: string = 'USD';

    initialPrice!: number;
    currentPrice!: number;

    initialBilledPrice!: string;

    activePlansCount!: number;
    userGeo = Utils.getNavigatorLanguage().replace(/-/g, '_');
    maxSlotsReached: boolean = false;
    licenseData!: LicenseData;
    useNewTestProduct: boolean = false;

    switchToAnnual: boolean = false;

    //ngAfterViewInit() {
    //    this.accountService.getCurrentUserLicense()
    //        .then(data => {
    //            this.licenseData = data;
    //            this.initLicenseData(this.licenseData);

    //            this.planService.GetActivePlansCountByLicense(this.licenseData.licenseID)
    //                .then((x: IGetActivePlansCountResponse | IErrorResponse) => {
    //                    if (isSuccessResponse(x)) {
    //                        x = x as IGetActivePlansCountResponse;
    //                        this.activePlansCount = x.count;
    //                    }
    //                });
    //        });
    //}

    ngOnInit() {
        // Subscribe on the redux store
        this.subscribeReduxStore();

        this.accountService.getCurrentUserLicense()
            .then(data => {
                this.licenseData = data;
                this.initLicenseData(this.licenseData);

                this.planService.GetActivePlansCountByLicense(this.licenseData.licenseID)
                    .then((x: IGetActivePlansCountResponse | IErrorResponse) => {
                        if (isSuccessResponse(x)) {
                            x = x as IGetActivePlansCountResponse;
                            this.activePlansCount = x.count;
                        }
                    });
            });
    }

    ngOnDestroy() {
        // Unsubscribe from the redux store
        this.storeUnsubscribe.next();
        this.storeUnsubscribe.complete();
    }

    // Subscribe on the redux store
    private subscribeReduxStore(): void {

        this.accountData$.pipe(
            takeUntil(this.storeUnsubscribe))
            .subscribe(x => this.accountData = x);

        this.authData$?.pipe(
            takeUntil(this.storeUnsubscribe))
            ?.subscribe((x: AuthData) => {
                this.authData = x;
            });

    }

    initLicenseData(data: LicenseData) {
        this.initialSubscriptionType = AuthUtils.getLicenseFrequency(data.priceOptionCodes);
        this.selectedSubscriptionType = this.initialSubscriptionType;

        this.initialPackageType = AuthUtils.getLicensePackageType(data.priceOptionCodes);
        if (data.licenseStatus)
            this.initialLicenseStatus = data.licenseStatus;
        if (data.licenseEndDate)
            this.initialLicenseEndDate = data.licenseEndDate;

        this.initialPlanCount = this.getInitialPlansCount(data.maxPlans);
        this.maxSlotsReached = this.initialPlanCount >= 50 || this.initialPlanCount == 0;

        this.initialPriceOptionCodes = data.priceOptionCodes;        

        let currencyId = currencyUtcSymbols.find(c => c.value == data.subscriptionCurrencyCode)?.id;

        if (currencyId != undefined) {

            let productCode = this.useNewTestProduct ? NavigationUtils.singleTestProductCode : NavigationUtils.productCode;
            this.billingService.GetPricesTable(productCode).then(data => {
                this.prices = JSON.parse(data);
                this.selectCurrency(currencyId);
                let planItem = this.brixxSinglePlansCountItems.find(p => p.elementId == this.initialPlanCount);

                if (planItem) {
                    this.initialPrice = this.getPrice(planItem.id);
                    this.initialBilledPrice = this.getBilledPrice();
                }

                this.brixxSinglePlansCountItems = this.brixxSinglePlansCountItems.filter(x => x.elementId && x.elementId > this.initialPlanCount);

                if (this.brixxSinglePlansCountItems.length > 0)
                    this.selectedPlansItemId = this.brixxSinglePlansCountItems[0].id;
            })
        }
    }

    getInitialPlansCount(maxPlans: number): number {

        let planSlots = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 15, 20, 30, 40, 50, 10000];
        let availableSlots = planSlots.filter(p => p <= maxPlans);
        if (availableSlots.length > 0)
            return availableSlots[availableSlots.length - 1];
        else
            return 0;
    }

    getBillingCycleinfo(frequency: string): BillingCycleInfo {
        let startDate = moment(this.licenseData.licenseStartDate);
        let curDate = moment();

        let startDay = startDate.date();
        let startMonth = startDate.month();
        let startYear = startDate.year();

        let curYear = moment().year();
        let curMonth = moment().month();
        let curDay = moment().date();

        let result = BillingCycleInfo;

        //console.log('license start date: ' + startDate, 'current date: ' + curDate);

        if (frequency == "Monthly") {
            if (curDay >= startDay)
                return {
                    // billing cycle start in current month
                    currentDay: curDay - startDay,
                    remainingDays: moment({ day: startDay, month: curMonth + 1, year: curYear }).diff(curDate, 'day')
                };
            else
                return {
                    // billing cycle start in previous month
                    currentDay: curDate.diff(moment({ day: startDay, month: curMonth - 1, year: curYear }), 'day'),
                    remainingDays: startDay - curDay
                }
        }
        else {
            let startDateCurYear = moment({ day: startDay, month: startMonth, year: curYear });
            let startDatePrevYear = moment({ day: startDay, month: startMonth, year: curYear - 1 });
            let curCycleDay = 0;

            if (startDateCurYear < curDate)
                // billing cycle start in current year
                curCycleDay = curDate.diff(startDateCurYear, 'day');
            else
                // billing cycle start in prev years
                curCycleDay = curDate.diff(startDatePrevYear, 'day');

            return {
                currentDay: curCycleDay,
                remainingDays: 365 - curCycleDay
            }
        }

    }

    getPrice(plansItemId: number): number {
        if (!plansItemId || !this.prices)
            return 0;

        const price = this.selectedSubscriptionType == "Monthly"
            ? parseFloat(this.prices[this.selectedCurCurrency][0][plansItemId - 1])
            : parseFloat(this.prices[this.selectedCurCurrency][1][plansItemId - 1]);

        return parseFloat(price.toFixed(2));
    }

    changeSinglePlansCount(value: string) {
        if (!value)
            return;

        let planItem = this.brixxSinglePlansCountItems.find(p => p.id == value);

        if (planItem) {
            let planItemCount = planItem.elementId;

            this.selectedPlansCount = planItemCount ? planItemCount.toString() : this.selectedPlansCount;

            if (this.prices !== undefined) {
                this.currentPrice = this.getPrice(parseInt(value));
                let curBillingCycle = this.getBillingCycleinfo(this.selectedSubscriptionType);
                //console.log('current cycle info, curDay:' + curBillingCycle.currentDay + ' , remain: ' + curBillingCycle.remainingDays);
            }
        }
    }

    private getCurrencyUtcSymbol(currencyId: number): string {
        const symbol: string | undefined = currencyUtcSymbols.find(currency => currency.id == currencyId)?.value;
        if (symbol !== undefined)
            return symbol;

        console.error('getCurrencyUtcSymbol() error: could not find currency symbol in currencyUtcSymbols, currencyId=', currencyId, ', previous value was returned');
        return this.currentCurSymbol;
    }

    private getCurrencySymbol(currencyId: number): string {
        const symbol: string | undefined = currencySymbols.find(currency => currency.id == currencyId)?.value;
        if (symbol !== undefined)
            return symbol;

        console.error('getCurrencySymbol() error: could not find currency symbol in currencySymbols, currencyId=', currencyId, ', previous value was returned');
        return this.currentCurSymbol;
    }

    selectCurrency(currencyId: number): void {
        this.selectedCurCurrency = this.getCurrencyUtcSymbol(currencyId);
        this.currentCurSymbol = this.getCurrencySymbol(currencyId);
        this.currentCurrencyId = currencyId;

        for (let i = 0; i < currencyUtcSymbols.length; i++) {
            currencyUtcSymbols[i].disabled = currencyUtcSymbols[i].id == currencyId;
        }

        let planItem = this.brixxSinglePlansCountItems.find(p => p.elementId == parseInt(this.selectedPlansCount));
        if (planItem)
            this.changeSinglePlansCount(planItem?.id);
    }

    onBuyPlanSlots(): void {
        let packageOptionUniqueCode = LicensePackageType.BrixxSingle + this.selectedPlansCount;
        let productCode = this.useNewTestProduct ? NavigationUtils.singleTestProductCode : NavigationUtils.productCode;

        NavigationUtils.openChangePaymentPage(
            this.accountData,
            packageOptionUniqueCode,
            this.selectedSubscriptionType,
            this.selectedCurCurrency,
            this.redirectService.getAccountUrl(),
            productCode,
            '2.0',
            this.licenseData.subscriptionID,
            this.licenseData.licenseCompanies
        );
    }

    onAnnualSwitched(v: { value: boolean, userChanged?: boolean }) {
        if (!v.userChanged)
            return;

        this.selectedSubscriptionType = v.value ? 'Annually' : 'Monthly';
        let curBillingCycle = this.getBillingCycleinfo(this.selectedSubscriptionType);
        let selectedPlanCountIndex = this.brixxSinglePlansCountItems.find(x => x.elementId == parseInt(this.selectedPlansCount))?.id
        this.currentPrice = this.getPrice(selectedPlanCountIndex);
    }

    public getBilledPrice(): string {
        const priceSingle = this.initialPrice;
        const billedPrice = this.initialSubscriptionType == 'Monthly' ? priceSingle : priceSingle * 12;

        return billedPrice.toFixed(2);
    }

    public formattedInitialPrice(): string {
        return this.initialPrice?.toFixed(2) ?? '0';
    }
}